import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OpsVoucherDiscountType } from '../../../../libs/api-client/src/generated/models';
import type { CreateVoucherRequestDto } from '../../../../libs/api-client/src/generated/models';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PageBody, PageHead } from '../components/Page';
import { Badge } from '../components/Pill';
import { StatusBanner } from '../components/StatusBanner';
import { intlLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import type { SupportedLocale } from '../i18n';
import { opsErrorMessage } from '../lib/ops-api';
import {
  MAX_VOUCHER_DURATION_INTERVALS,
  VOUCHER_DESCRIPTION_MAX_LENGTH,
  VOUCHER_TIERS,
  emptyVoucherDraft,
  formatDiscount,
  normaliseVoucherCode,
  redeemabilityBadge,
  usageFiguresDisagree,
  validateVoucherDraft,
  type VoucherDraft,
  type VoucherDraftErrors,
} from '../lib/ops-billing';
import {
  createVoucher,
  deactivateVoucher,
  fetchVoucherUsages,
  fetchVouchers,
  type OpsVoucher,
} from '../lib/ops-billing-api';
import {
  REASON_MIN_LENGTH,
  isReasonAcceptable,
  shopFilePath,
} from '../lib/shop-file';

/** A date as the reader's locale writes it, or a dash. */
function day(
  value: string | null | undefined,
  locale: SupportedLocale,
): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(intlLocale(locale), {
    dateStyle: 'medium',
  });
}

/**
 * Voucher codes (work order AC-7.2, AC-7.4, AC-7.5): list with both usage figures, create,
 * deactivate, and who redeemed a code.
 *
 * Three facts sit at the top of the page because each one has already surprised somebody:
 * a code only reaches subscriptions created AFTER it (the discount rides `appSubscriptionCreate`),
 * deactivating a code does not take the discount off a subscription that already has it, and
 * there is no delete — `VoucherCodeUsage` cascades, so deleting a code would erase who used it.
 */
export function VouchersPage() {
  const { t, tCount, tNode, locale } = useLocale();
  const [openUsagesFor, setOpenUsagesFor] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<OpsVoucher | null>(null);
  const [lastAudit, setLastAudit] = useState<{
    readonly code: string;
    readonly auditLogId: string;
    readonly kind: 'created' | 'deactivated';
  } | null>(null);

  const vouchers = useQuery({
    queryKey: ['ops-vouchers'],
    queryFn: fetchVouchers,
  });
  // Until the list answers, the billing currency the API always reports (BILLING_CURRENCY) —
  // the form needs a unit before the first response, and there is only one.
  const currency = vouchers.data?.billingCurrency ?? 'USD';
  const intl = intlLocale(locale);

  return (
    <>
      <PageHead
        eyebrow={t('common.operations')}
        title={t('vouchers.title')}
        lead={t('vouchers.lead')}
      />
      <PageBody>
        <StatusBanner tone="info">
          <ul className="list-disc pl-5 space-y-1">
            <li>{t('vouchers.factFuture')}</li>
            <li>{t('vouchers.factDeactivate')}</li>
            <li>{t('vouchers.factNoDelete')}</li>
            <li>
              {tNode('vouchers.factCurrency', {
                currency: <strong>{currency}</strong>,
              })}
            </li>
          </ul>
        </StatusBanner>

        {lastAudit && (
          <StatusBanner tone="success">
            <div className="space-y-1">
              <p>
                {lastAudit.kind === 'created'
                  ? t('vouchers.created', { code: lastAudit.code })
                  : t('vouchers.deactivated', { code: lastAudit.code })}
              </p>
              <p className="font-mono text-xs">
                {t('vouchers.auditRow', { id: lastAudit.auditLogId })}
              </p>
            </div>
          </StatusBanner>
        )}

        <CreateVoucherForm
          currency={currency}
          onCreated={(code, auditLogId) =>
            setLastAudit({ code, auditLogId, kind: 'created' })
          }
        />

        <section className="card space-y-3">
          <h2 className="text-base font-semibold text-gray-900">
            {t('vouchers.list.title')}
          </h2>
          {vouchers.isError ? (
            <StatusBanner tone="error">
              {opsErrorMessage(vouchers.error, t('vouchers.list.loadFailed'))}
            </StatusBanner>
          ) : !vouchers.data ? (
            <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
          ) : vouchers.data.data.length === 0 ? (
            <p className="text-sm text-gray-600">{t('vouchers.list.none')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('vouchers.list.code')}</th>
                    <th>{t('vouchers.list.discount')}</th>
                    <th>{t('vouchers.list.validity')}</th>
                    <th>{t('vouchers.list.tiers')}</th>
                    <th>{t('vouchers.list.usage')}</th>
                    <th>{t('vouchers.list.state')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {vouchers.data.data.map((voucher) => {
                    const badge = redeemabilityBadge(voucher.redeemability);
                    return (
                      <tr key={voucher.id}>
                        <td>
                          <span className="font-mono">{voucher.code}</span>
                          {voucher.description && (
                            <p className="text-xs text-gray-500">
                              {voucher.description}
                            </p>
                          )}
                        </td>
                        <td className="whitespace-nowrap">
                          {formatDiscount(
                            voucher.discountType,
                            voucher.discountValue,
                            voucher.currency,
                            intl,
                          )}
                          <p className="text-xs text-gray-500">
                            {voucher.durationLimitInIntervals === null ||
                            voucher.durationLimitInIntervals === undefined
                              ? t('vouchers.list.durationUnlimited')
                              : tCount(
                                  'vouchers.list.duration',
                                  voucher.durationLimitInIntervals,
                                )}
                          </p>
                        </td>
                        <td className="whitespace-nowrap text-sm">
                          {day(voucher.validFrom, locale)} –{' '}
                          {voucher.validUntil
                            ? day(voucher.validUntil, locale)
                            : t('vouchers.list.noEnd')}
                        </td>
                        <td className="text-xs">
                          {voucher.applicableTiers.length === 0
                            ? t('vouchers.list.allTiers')
                            : voucher.applicableTiers.join(', ')}
                        </td>
                        <td className="text-sm">
                          <p className="tabular-nums">
                            {voucher.maxUsage === null ||
                            voucher.maxUsage === undefined
                              ? t('vouchers.list.counterUnlimited', {
                                  count: voucher.currentUsage,
                                })
                              : t('vouchers.list.counterOfMax', {
                                  count: voucher.currentUsage,
                                  max: voucher.maxUsage,
                                })}
                          </p>
                          <p className="text-xs text-gray-500 tabular-nums">
                            {tCount(
                              'vouchers.list.rows',
                              voucher.usageRowCount,
                            )}
                          </p>
                          {usageFiguresDisagree(voucher) && (
                            <Badge tone="amber">
                              {t('vouchers.list.disagree')}
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Badge tone={badge.tone}>{t(badge.labelKey)}</Badge>
                        </td>
                        <td className="whitespace-nowrap space-x-2">
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() =>
                              setOpenUsagesFor(
                                openUsagesFor === voucher.id
                                  ? null
                                  : voucher.id,
                              )
                            }
                          >
                            {openUsagesFor === voucher.id
                              ? t('vouchers.list.hideUsages')
                              : t('vouchers.list.showUsages')}
                          </button>
                          {voucher.isActive && (
                            <button
                              type="button"
                              className="btn-neutral btn-sm"
                              onClick={() => setDeactivating(voucher)}
                            >
                              {t('vouchers.list.deactivate')}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {vouchers.data && (
            <p className="field-hint">{t('vouchers.list.usageHint')}</p>
          )}
        </section>

        {openUsagesFor && <VoucherUsages voucherId={openUsagesFor} />}
      </PageBody>

      <DeactivateDialog
        voucher={deactivating}
        onClose={() => setDeactivating(null)}
        onDone={(code, auditLogId) =>
          setLastAudit({ code, auditLogId, kind: 'deactivated' })
        }
      />
    </>
  );
}

/** Text input bound to one draft field, with its error or hint. */
function DraftField({
  id,
  label,
  hint,
  error,
  value,
  onChange,
  type = 'text',
  className = '',
}: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly type?: 'text' | 'date';
  readonly className?: string;
}) {
  const { t } = useLocale();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`field-input ${className}`}
      />
      {error ? (
        <p className="field-error">
          {t(error, { max: MAX_VOUCHER_DURATION_INTERVALS })}
        </p>
      ) : (
        hint && <p className="field-hint">{hint}</p>
      )}
    </div>
  );
}

/** The create form, a review dialog, then the audited create. */
function CreateVoucherForm({
  currency,
  onCreated,
}: {
  readonly currency: string;
  readonly onCreated: (code: string, auditLogId: string) => void;
}) {
  const { t, tCount, locale } = useLocale();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<VoucherDraft>(emptyVoucherDraft);
  const [errors, setErrors] = useState<VoucherDraftErrors>({});
  const [review, setReview] = useState<CreateVoucherRequestDto | null>(null);

  const create = useMutation({
    mutationFn: createVoucher,
    onSuccess: (result) => {
      onCreated(result.voucher.code, result.auditLogId);
      setDraft(emptyVoucherDraft());
      setErrors({});
      void queryClient.invalidateQueries({ queryKey: ['ops-vouchers'] });
    },
  });

  function set<K extends keyof VoucherDraft>(
    key: K,
    value: VoucherDraft[K],
  ): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    const result = validateVoucherDraft(draft);
    setErrors(result.errors);
    if (result.payload) setReview(result.payload);
  }

  const unit =
    draft.discountType === OpsVoucherDiscountType.Percentage ? '%' : currency;

  return (
    <section className="card space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">
          {t('vouchers.form.title')}
        </h2>
        <p className="text-sm text-gray-600">{t('vouchers.form.lead')}</p>
      </div>
      <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
        <DraftField
          id="voucherCode"
          label={t('vouchers.form.code')}
          hint={t('vouchers.form.codeHint', {
            code: normaliseVoucherCode(draft.code) || '—',
          })}
          error={errors.code}
          value={draft.code}
          onChange={(value) => set('code', value)}
          className="font-mono"
        />

        <div className="space-y-1">
          <span className="field-label">{t('vouchers.form.type')}</span>
          <div className="flex gap-4 text-sm">
            {[
              OpsVoucherDiscountType.Percentage,
              OpsVoucherDiscountType.FixedAmount,
            ].map((type) => (
              <label key={type} className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="voucherType"
                  checked={draft.discountType === type}
                  onChange={() => set('discountType', type)}
                />
                {type === OpsVoucherDiscountType.Percentage
                  ? t('vouchers.form.typePercent')
                  : t('vouchers.form.typeFixed', { currency })}
              </label>
            ))}
          </div>
        </div>

        <DraftField
          id="voucherValue"
          label={t('vouchers.form.value', { unit })}
          hint={
            draft.discountType === OpsVoucherDiscountType.Percentage
              ? t('vouchers.form.valueHintPercent')
              : t('vouchers.form.valueHintFixed', { currency })
          }
          error={errors.discountValue}
          value={draft.discountValue}
          onChange={(value) => set('discountValue', value)}
        />

        <DraftField
          id="voucherDuration"
          label={t('vouchers.form.duration')}
          hint={t('vouchers.form.durationHint', {
            max: MAX_VOUCHER_DURATION_INTERVALS,
          })}
          error={errors.durationLimitInIntervals}
          value={draft.durationLimitInIntervals}
          onChange={(value) => set('durationLimitInIntervals', value)}
        />

        <DraftField
          id="voucherFrom"
          type="date"
          label={t('vouchers.form.validFrom')}
          hint={t('vouchers.form.validFromHint')}
          error={errors.validFrom}
          value={draft.validFrom}
          onChange={(value) => set('validFrom', value)}
        />

        <DraftField
          id="voucherUntil"
          type="date"
          label={t('vouchers.form.validUntil')}
          hint={t('vouchers.form.validUntilHint')}
          error={errors.validUntil}
          value={draft.validUntil}
          onChange={(value) => set('validUntil', value)}
        />

        <DraftField
          id="voucherMaxUsage"
          label={t('vouchers.form.maxUsage')}
          hint={t('vouchers.form.maxUsageHint')}
          error={errors.maxUsage}
          value={draft.maxUsage}
          onChange={(value) => set('maxUsage', value)}
        />

        <div className="space-y-1">
          <span className="field-label">{t('vouchers.form.tiers')}</span>
          <div className="flex flex-wrap gap-4 text-sm">
            {VOUCHER_TIERS.map((tier) => (
              <label key={tier} className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.applicableTiers.includes(tier)}
                  onChange={(event) =>
                    set(
                      'applicableTiers',
                      event.target.checked
                        ? VOUCHER_TIERS.filter(
                            (x) =>
                              x === tier || draft.applicableTiers.includes(x),
                          )
                        : draft.applicableTiers.filter((x) => x !== tier),
                    )
                  }
                />
                {tier}
              </label>
            ))}
          </div>
          <p className="field-hint">{t('vouchers.form.tiersHint')}</p>
        </div>

        <div className="space-y-1 md:col-span-2">
          <label htmlFor="voucherDescription" className="field-label">
            {t('vouchers.form.description')}
          </label>
          <input
            id="voucherDescription"
            value={draft.description}
            maxLength={VOUCHER_DESCRIPTION_MAX_LENGTH}
            onChange={(event) => set('description', event.target.value)}
            className="field-input"
          />
          <p className="field-hint">{t('vouchers.form.descriptionHint')}</p>
        </div>

        <div className="space-y-1 md:col-span-2">
          <label htmlFor="voucherReason" className="field-label">
            {t('vouchers.reasonLabel')}
          </label>
          <textarea
            id="voucherReason"
            value={draft.reason}
            rows={2}
            maxLength={500}
            onChange={(event) => set('reason', event.target.value)}
            placeholder={t('vouchers.form.reasonPlaceholder')}
            className="field-input"
          />
          {errors.reason ? (
            <p className="field-error">{t(errors.reason)}</p>
          ) : (
            <p className="field-hint">
              {t('vouchers.reasonHint', { min: REASON_MIN_LENGTH })}
            </p>
          )}
        </div>

        <div className="md:col-span-2 space-y-3">
          <button
            type="submit"
            className="btn-primary btn-sm"
            disabled={create.isPending}
          >
            {create.isPending
              ? t('vouchers.form.creating')
              : t('vouchers.form.submit')}
          </button>
          {create.isError && (
            <StatusBanner tone="error">
              {opsErrorMessage(create.error, t('vouchers.form.failed'))}
            </StatusBanner>
          )}
        </div>
      </form>

      <ConfirmDialog
        open={review !== null}
        title={t('vouchers.form.confirmTitle')}
        confirmLabel={t('vouchers.form.confirm')}
        onCancel={() => setReview(null)}
        onConfirm={() => {
          const payload = review;
          setReview(null);
          if (payload) create.mutate(payload);
        }}
      >
        {review && (
          <div className="space-y-2">
            <p>
              {t('vouchers.form.confirmSummary', {
                code: review.code,
                discount: formatDiscount(
                  review.discountType,
                  review.discountValue,
                  currency,
                  intlLocale(locale),
                ),
              })}
            </p>
            <p>
              {review.durationLimitInIntervals === null ||
              review.durationLimitInIntervals === undefined
                ? t('vouchers.list.durationUnlimited')
                : tCount(
                    'vouchers.list.duration',
                    review.durationLimitInIntervals,
                  )}
              {' · '}
              {review.applicableTiers && review.applicableTiers.length > 0
                ? review.applicableTiers.join(', ')
                : t('vouchers.list.allTiers')}
            </p>
            <p>{t('vouchers.factFuture')}</p>
            <p className="text-gray-500">{t('vouchers.confirmAudit')}</p>
          </div>
        )}
      </ConfirmDialog>
    </section>
  );
}

/** Deactivation: the reason is typed IN the dialog, which holds its confirm until it is long enough. */
function DeactivateDialog({
  voucher,
  onClose,
  onDone,
}: {
  readonly voucher: OpsVoucher | null;
  readonly onClose: () => void;
  readonly onDone: (code: string, auditLogId: string) => void;
}) {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const deactivate = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      deactivateVoucher(input.id, input.reason),
    onSuccess: (result) => {
      onDone(result.voucher.code, result.auditLogId);
      setReason('');
      onClose();
      void queryClient.invalidateQueries({ queryKey: ['ops-vouchers'] });
    },
  });

  return (
    <ConfirmDialog
      open={voucher !== null}
      title={t('vouchers.deactivateDialog.title', {
        code: voucher?.code ?? '',
      })}
      confirmLabel={t('vouchers.deactivateDialog.confirm')}
      confirmDisabled={!isReasonAcceptable(reason) || deactivate.isPending}
      onCancel={() => {
        setReason('');
        deactivate.reset();
        onClose();
      }}
      onConfirm={() => {
        if (voucher)
          deactivate.mutate({ id: voucher.id, reason: reason.trim() });
      }}
    >
      <div className="space-y-3">
        <p>{t('vouchers.deactivateDialog.body')}</p>
        <p>{t('vouchers.factDeactivate')}</p>
        <div className="space-y-1">
          <label htmlFor="voucherDeactivateReason" className="field-label">
            {t('vouchers.reasonLabel')}
          </label>
          <textarea
            id="voucherDeactivateReason"
            value={reason}
            rows={2}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
            className="field-input"
          />
          <p className="field-hint">
            {t('vouchers.reasonHint', { min: REASON_MIN_LENGTH })}
          </p>
        </div>
        {deactivate.isError && (
          <StatusBanner tone="error">
            {opsErrorMessage(
              deactivate.error,
              t('vouchers.deactivateDialog.failed'),
            )}
          </StatusBanner>
        )}
      </div>
    </ConfirmDialog>
  );
}

/** Who redeemed one code, each shop linked to its file. */
function VoucherUsages({ voucherId }: { readonly voucherId: string }) {
  const { t, locale } = useLocale();
  const usages = useQuery({
    queryKey: ['ops-voucher-usages', voucherId],
    queryFn: () => fetchVoucherUsages(voucherId),
  });

  return (
    <section className="card space-y-3">
      <h2 className="text-base font-semibold text-gray-900">
        {t('vouchers.usages.title', { code: usages.data?.code ?? '…' })}
      </h2>
      {usages.isError ? (
        <StatusBanner tone="error">
          {opsErrorMessage(usages.error, t('vouchers.usages.loadFailed'))}
        </StatusBanner>
      ) : !usages.data ? (
        <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            {t('vouchers.usages.counts', {
              counter: usages.data.currentUsage,
              rows: usages.data.usageRowCount,
            })}
          </p>
          {usages.data.data.length === 0 ? (
            <p className="text-sm text-gray-600">{t('vouchers.usages.none')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('vouchers.usages.shop')}</th>
                    <th>{t('vouchers.usages.subscription')}</th>
                    <th>{t('vouchers.usages.current')}</th>
                    <th>{t('vouchers.usages.usedAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {usages.data.data.map((usage) => (
                    <tr key={usage.id}>
                      <td>
                        <Link
                          to={shopFilePath(usage.shopId, 'billing')}
                          className="font-mono text-primary-700"
                        >
                          {usage.shopDomain}
                        </Link>
                      </td>
                      <td className="font-mono text-xs">
                        {usage.subscriptionId ?? '—'}
                      </td>
                      <td>
                        <Badge
                          tone={usage.onCurrentSubscription ? 'green' : 'gray'}
                        >
                          {usage.onCurrentSubscription
                            ? t('common.yes')
                            : t('common.no')}
                        </Badge>
                      </td>
                      <td>{day(usage.usedAt, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {usages.data.truncated && (
            <p className="field-hint">{t('vouchers.usages.truncated')}</p>
          )}
        </>
      )}
    </section>
  );
}
